import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import ArrowIcon from '../ui/ArrowIcon';
import './Hero.css';

const PUB = process.env.PUBLIC_URL;
const EASE = [0.22, 1, 0.36, 1];

// Boxes are percentages of the illustration (1024×1536)
const DETECTIONS = [
  { id: 'person', label: 'person', conf: 0.99, box: { x: 3, y: 8, w: 80, h: 92 }, tone: 'accent' },
  { id: 'laptop', label: 'laptop', conf: 0.94, box: { x: 37, y: 66, w: 63, h: 34 }, tone: 'magenta' },
  { id: 'window', label: 'window', conf: 0.88, box: { x: 89, y: 15, w: 11, h: 42 }, tone: 'yellow' },
];

const CAPTION = "A computer engineer at her desk, building systems that see what's in the frame — and understand what it means.";

const SOCIAL = [
  { label: 'GitHub', href: 'https://github.com/nevaerdogan' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/nevaerdogan/' },
  { label: 'Medium', href: 'https://medium.com/@nevardogan' },
  { label: 'Email', href: 'mailto:nevardogan@hotmail.com' },
];

// Counts a confidence score up from 0.00 once `active` flips true
const useCountUp = (target, active, duration = 700) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) { setValue(0); return undefined; }
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return value;
};

const Box = ({ d, index, show }) => {
  const conf = useCountUp(d.conf, show);
  return (
    <motion.div
      className={`scene-box tone-${d.tone}`}
      style={{ left: `${d.box.x}%`, top: `${d.box.y}%`, width: `${d.box.w}%`, height: `${d.box.h}%` }}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: show ? 1 : 0, scale: show ? 1 : 0.96 }}
      transition={{ duration: 0.45, ease: EASE, delay: show ? index * 0.18 : 0 }}
      aria-hidden="true"
    >
      <span className="scene-tag mono">{d.label} <b>{conf.toFixed(2)}</b></span>
    </motion.div>
  );
};

const Hero = () => {
  const reduce = useReducedMotion();
  const [phase, setPhase] = useState('idle');   // idle → scan → boxes
  const [mode, setMode] = useState('detect');   // detect | understand
  const touched = useRef(false);

  // Orchestrated load: scan the frame, draw boxes, then step up to understanding
  useEffect(() => {
    const t1 = setTimeout(() => setPhase('scan'), reduce ? 0 : 500);
    const t2 = setTimeout(() => setPhase('boxes'), reduce ? 0 : 1500);
    const t3 = setTimeout(() => { if (!touched.current) setMode('understand'); }, reduce ? 2500 : 4800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [reduce]);

  const choose = (m) => {
    touched.current = true;
    setMode(m);
    if (phase !== 'boxes') setPhase('boxes');
  };

  const showBoxes = phase === 'boxes' && mode === 'detect';

  const fade = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } } };
  const rise = reduce ? fade : { hidden: { y: '105%' }, show: { y: 0, transition: { duration: 0.9, ease: EASE } } };

  return (
    <section id="home" className="hero">
      <div className="container hero-grid">
        <motion.div
          className="hero-copy hero-copy--top"
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.09, delayChildren: 0.15 } } }}
        >
          <motion.span className="eyebrow" variants={fade}>Computer Engineer</motion.span>
          <h1 className="t-display hero-name">
            <span className="hero-name-line"><motion.span variants={rise}>Neva</motion.span></span>
            <span className="hero-name-line"><motion.span variants={rise}>Erdoğan</motion.span></span>
          </h1>
          <motion.p className="hero-sub" variants={fade}>
            I build vision-language systems that go <em>beyond the bounding box.</em>
          </motion.p>
        </motion.div>

        <motion.div
          className="hero-copy hero-copy--bottom"
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.09, delayChildren: 0.5 } } }}
        >
          <motion.p className="hero-body" variants={fade}>
            Currently at Ottovate, working on contextual scene understanding — detection models that
            know what they're looking at. Before that: fine-tuned NLP models and product ownership at
            PSH, and a multi-agent forecasting system that beat Wall Street consensus at a London hackathon.
          </motion.p>
          <motion.ul className="hero-links" variants={fade}>
            {SOCIAL.map((s) => (
              <li key={s.label}>
                <a
                  href={s.href}
                  className="link-arrow"
                  target={s.href.startsWith('http') ? '_blank' : undefined}
                  rel={s.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                >
                  {s.label} <ArrowIcon />
                </a>
              </li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div
          className="hero-scene"
          initial={{ opacity: 0, y: 30, rotate: reduce ? 0 : -1.5 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.3 }}
        >
          <div className="scene">
            <picture>
              <source srcSet={`${PUB}/hero.webp`} type="image/webp" />
              <img
                src={`${PUB}/hero.jpg`}
                alt="Illustration of Neva at a desk with a laptop"
                className="scene-img"
                width="1024"
                height="1536"
                fetchPriority="high"
              />
            </picture>
            <div className="scene-vignette" aria-hidden="true" />

            <AnimatePresence>
              {phase === 'scan' && !reduce && (
                <motion.div
                  className="scene-scan"
                  initial={{ top: '-4%' }}
                  animate={{ top: '104%' }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1, ease: 'linear' }}
                  aria-hidden="true"
                />
              )}
            </AnimatePresence>

            {DETECTIONS.map((d, i) => <Box key={d.id} d={d} index={i} show={showBoxes} />)}

            <AnimatePresence>
              {mode === 'understand' && (
                <motion.div
                  className="scene-caption"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 12 }}
                  transition={{ duration: 0.55, ease: EASE }}
                >
                  <span className="scene-caption-eyebrow mono">scene understanding</span>
                  <p>{CAPTION}</p>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {mode === 'detect' && (
                <motion.div
                  className="scene-status mono"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.3 }}
                >
                  <span className={`scene-status-dot ${phase !== 'idle' ? 'is-on' : ''}`} aria-hidden="true" />
                  mode: detect
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="scene-toggle" role="group" aria-label="Scene view">
            {['detect', 'understand'].map((m) => (
              <button
                key={m}
                type="button"
                className={`scene-toggle-btn ${mode === m ? 'is-active' : ''}`}
                onClick={() => choose(m)}
                aria-pressed={mode === m}
              >
                {mode === m && (
                  <motion.span
                    layoutId="scene-toggle-pill"
                    className="scene-toggle-pill"
                    transition={{ type: 'spring', stiffness: 400, damping: 34 }}
                  />
                )}
                <span className="scene-toggle-label">{m === 'detect' ? 'Detect' : 'Understand'}</span>
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
